import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { resetStores, setLoading } from '@datorama/akita';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { SDKConfig } from '../config/sdk-config.interface';
import { SDK_CONFIG_TOKEN } from '../config/sdk.config.token';
import { MessageService } from '../message';
import { STORAGE_KEYS } from '../shared/constants';
import { User, UserPreference } from '../user';
import { AuthStore } from './auth.store';
import { LoginResponse } from './interfaces';

export function serializePreferences(
  preferences: UserPreference[] = []
): UserPreference[] {
  return preferences.map((pref) => {
    let value: any;

    try {
      value = JSON.parse(pref.value);
    } catch (err) {
      value = null;
    }

    return {
      ...pref,
      value,
    };
  });
}

@Injectable()
export class AuthService {
  private get _resourceURI() {
    return `${this.config.gatewayUrl}/users`;
  }

  constructor(
    private store: AuthStore,
    private http: HttpClient,
    private messageService: MessageService,
    @Inject(SDK_CONFIG_TOKEN) private config: SDKConfig
  ) {}

  login(props: { email: string; password: string }) {
    this.store.update((state) => ({ ...state, isLoggingIn: true }));
    return this.http
      .post<LoginResponse>(`${this.config.gatewayUrl}/tokens`, props)
      .pipe(
        setLoading(this.store),
        map((response) => {
          return this._handleLogin(response.token, response.user)
            ? response
            : throwError(response);
        }),
        catchError((error) => {
          this.store.setError(error);
          this.store.update((state) => ({ ...state, isLoggingIn: false }));
          return throwError(error);
        })
      );
  }

  loginWithGoogle(token: string): Observable<any> {
    this.store.update((state) => ({ ...state, isLoggingIn: true }));
    return this.http
      .post<LoginResponse>(`${this.config.gatewayUrl}/tokens/google`, { token })
      .pipe(
        setLoading(this.store),
        map((response) => {
          return this._handleLogin(response.token, response.user)
            ? response
            : throwError(response);
        }),
        catchError((error) => {
          this.store.setError(error);
          this.store.update((state) => ({ ...state, isLoggingIn: false }));
          return throwError(error);
        })
      );
  }

  reloadUser() {
    return this.loginStoredUser(this.store.getValue().token);
  }

  updateUser(user: User) {
    this.store.update((state) => ({
      ...state,
      user,
    }));
  }

  private _handleLogin(token?: string, user?: User) {
    if (!token) {
      this.store.update(
        (state) =>
          ({
            ...state,
            token: null,
            user: null,
            isLoggingIn: false,
            isLoggedIn: false,
            isRegistering: false,
          } as any)
      );
      return null;
    }

    if (user) {
      this.store.update((state) => ({
        ...state,
        token,
        user: {
          ...user,
          preferences: serializePreferences(user?.preferences),
        },
        isLoggedIn: true,
        isLoggingIn: false,
        isRegistering: false,
      }));
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    }

    return user;
  }

  loginStoredUser(token?: string, u?: User): Observable<User | null | any> {
    if (token) {
      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      return this.http
        .get<User>(`${this.config.gatewayUrl}/users`, { headers })
        .pipe(
          map((user) => this._handleLogin(token, user)),
          catchError((error) => this.loginStoredUser())
        );
    } else {
      return of(this._handleLogin());
    }
  }

  register(body: User) {
    this.store.update((state) => ({ ...state, isRegistering: true }));
    return this.http
      .post<User>(this._resourceURI, body, { observe: 'response' })
      .pipe(
        map((response) =>
          this._handleLogin(
            response.headers.get('x-access-token') as string,
            response.body as User
          )
        ),
        finalize(() =>
          this.store.update((state) => ({ ...state, isRegistering: false }))
        )
      );
  }

  logout(navigate = false) {
    if (navigate) {
    }
    localStorage.clear();
    this.messageService.unsubscribe();
    resetStores();
  }

  changePassword(password: string, newPassword: string) {
    return this.http.put(`${this._resourceURI}/password`, {
      password,
      newPassword,
    });
  }

  registerDeviceToken(token: string) {
    this.http.post(`${this._resourceURI}/device-token`, { token }).subscribe();
  }
}
